import { useEffect, useMemo } from 'react';
import { FormControl } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import type { AttributesLevels } from './LevelOfCareSection';
import type { ReservationFormInput } from '../schema/reservationForm.schema';
import type { TripArrayFieldContext } from '../machines/createTripArrayFieldMachine';

import { useReservationFormMachines } from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../hooks/useFieldActorSnapshot';

import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import SelectMenu from '@/components/forms/SelectMenu';
import { LevelOfCareContext } from '../machines/levelOfCare.machine';
import { R2R_BC } from '@/data/constants';
import { usePortalContext } from '@/contexts/PortalProvider';

export function PassengerAssistanceField({
  index,
  availableAttributes,
}: {
  index: number;
  availableAttributes: AttributesLevels;
}) {
  const t = useCommonTranslation();
  const { isClientPortal } = usePortalContext();
  const { control, trigger } = useFormContext<ReservationFormInput>();
  const { fieldRefs } = useReservationFormMachines();
  const actor = fieldRefs.passengerAssistance;
  const levelOfCareActor = fieldRefs.levelOfCare;
  const levelOfCareSnapshot = useFieldActorSnapshot(levelOfCareActor);
  const isGurneySelected =
    (levelOfCareSnapshot.context as LevelOfCareContext).isGurneySelected[
      index
    ] ?? false;

  const watched =
    useWatch({
      control,
      name: `trips.${index}.MobilityAttribute`,
    }) ?? '';

  useEffect(() => {
    const ctx = actor.getSnapshot().context as TripArrayFieldContext;
    if (ctx.values[index] !== watched) {
      actor.send({ type: 'CHANGE', index, value: watched });
    }
  }, [actor, index, watched]);

  const options = useMemo(() => {
    if (!isClientPortal) return availableAttributes?.assistance;

    return availableAttributes?.assistance?.map((attr) =>
      attr.passengerCareLevelBC === R2R_BC
        ? {
            ...attr,
            isDisabled: true,
          }
        : attr,
    );
  }, [isClientPortal, availableAttributes?.assistance]);

  return (
    <Controller
      name={`trips.${index}.MobilityAttribute`}
      control={control}
      render={({ field, fieldState }) => (
        <FormControl fullWidth required error={Boolean(fieldState.error)}>
          <SelectMenu
            label={t('reservation.passengerAssistance')}
            name={`trips.${index}.MobilityAttribute`}
            values={options ?? []}
            valueAttr="transportationAttributeUUID"
            labelAttr="name"
            required={true}
            disabled={isGurneySelected}
            disabledAttr="isDisabled"
            control={control}
            rules={{ required: true }}
            onChange={(evt) => {
              const v = String(evt.target.value);
              actor.send({ type: 'CHANGE', index, value: v });
              field.onChange(v);
              // Re-validate this leg so other required fields (e.g.
              // reasonForTrip) surface their resolver errors after the
              // mobility/assistance dropdowns — typically the last fields
              // the user touches — are changed. Scoped to this leg so it
              // doesn't surface errors on other trip legs.
              trigger(`trips.${index}`);
            }}
          />
        </FormControl>
      )}
    />
  );
}
